import { useCallback, useEffect, useRef, useState } from 'react';
import { getFileSyncManifest, postFileSyncLocalReport } from '../services/services';
import {
  PATH_MISMATCH_MESSAGE,
  chooseFolder,
  folderIsReadable,
  folderPermission,
  forgetFolderHandle,
  isHandleForPath,
  isFolderAccessSupported,
  loadFolderHandle,
  requestFolderPermission,
  runLocalFolderCheck,
  scanIsDue
} from '../utils/localFolderScan';

export const FOLDER_ACCESS_EVENT = 'bh-folder-access-changed';

// Wraps the two endpoints so a failed request is a thrown error the check can
// see, not an axios error object returned as if it were data.
const api = {
  getManifest: async (params) => {
    const res = await getFileSyncManifest(params);
    if (res?.status !== 200)
      throw new Error(res?.response?.data?.metadata?.message || 'manifest failed');
    return res.data?.payload?.data;
  },
  postReport: async (body) => {
    const res = await postFileSyncLocalReport(body);
    if (res?.status !== 200)
      throw new Error(res?.response?.data?.metadata?.message || 'report failed');
    return res.data?.payload?.data;
  }
};

/**
 * The local folder check for this company on THIS browser. The folder is the
 * one the company saved as "Add local path of documents folder" (`savedPath`)
 * and nothing else: a stored browser grant for any other folder — one allowed
 * before the path changed, or an old test folder — is discarded on load and
 * never checked. The only action is to allow access to that saved folder (or
 * to re-allow it, when the browser has forgotten); then it is checked here and,
 * with `auto`, in the background whenever the app is open.
 */
export default function useLocalFolderCheck(companyId, { auto = false, savedPath = '' } = {}) {
  const supported = isFolderAccessSupported();
  const [handle, setHandle] = useState(null);
  const [permission, setPermission] = useState('prompt');
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const runningRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!supported || !companyId) return;
    let h = await loadFolderHandle(companyId);
    if (h && savedPath && !isHandleForPath(h, savedPath)) {
      // Not the saved folder: forget it, so it can never be checked.
      await forgetFolderHandle(companyId).catch(() => {});
      h = null;
    }
    setHandle(h);
    setPermission(h ? await folderPermission(h) : 'prompt');
  }, [supported, companyId, savedPath]);

  const check = useCallback(
    async (h = handle) => {
      if (!h || runningRef.current) return null;
      runningRef.current = true;
      setRunning(true);
      try {
        const result = await runLocalFolderCheck({ companyId, handle: h, savedPath, api });
        setLastResult(result);
        return result;
      } catch (error) {
        const result = { error: error?.message || 'The folder check failed' };
        setLastResult(result);
        return result;
      } finally {
        runningRef.current = false;
        setRunning(false);
      }
    },
    [companyId, handle, savedPath]
  );

  /**
   * "Verify folder access". Must be called from a click (browser rule).
   *   - access already stored for the saved folder: ask the browser to allow
   *     it again if it has forgotten (its Allow / Don't allow popup), then make
   *     sure the folder is really there;
   *   - nothing stored yet: the browser's own dialog, once, where the saved
   *     folder is confirmed — any other folder is refused.
   * Returns {ok, message}. Never runs the check itself; that is "Check now".
   */
  const verify = useCallback(async () => {
    if (!savedPath) return { ok: false, message: 'Add the local folder path first.' };
    let h = handle;
    try {
      if (h) {
        let state = await folderPermission(h);
        if (state !== 'granted') state = (await requestFolderPermission(h)) ? 'granted' : 'denied';
        if (state !== 'granted') {
          setPermission('denied');
          return {
            ok: false,
            message: 'Access was not allowed, so this folder cannot be checked.'
          };
        }
      } else {
        h = await chooseFolder(companyId, savedPath);
      }
    } catch (error) {
      if (error?.name === 'AbortError') return { ok: false, cancelled: true };
      return { ok: false, message: error?.message || PATH_MISMATCH_MESSAGE };
    }
    if (!(await folderIsReadable(h))) {
      await forgetFolderHandle(companyId).catch(() => {});
      setHandle(null);
      setPermission('prompt');
      return { ok: false, message: PATH_MISMATCH_MESSAGE };
    }
    setHandle(h);
    setPermission('granted');
    window.dispatchEvent(new Event(FOLDER_ACCESS_EVENT));
    return { ok: true, handle: h, message: `Access verified for ${savedPath}.` };
  }, [handle, companyId, savedPath]);

  // The background prompt re-grants, then checks.
  const allow = useCallback(async () => {
    const r = await verify();
    return r.ok ? check(r.handle) : null;
  }, [verify, check]);

  useEffect(() => {
    refresh();
    // Another part of the page verified access: pick up the same grant.
    window.addEventListener(FOLDER_ACCESS_EVENT, refresh);
    return () => window.removeEventListener(FOLDER_ACCESS_EVENT, refresh);
  }, [refresh]);

  // The background check: on load, then hourly while the app stays open; it
  // only actually scans when SCAN_INTERVAL_HOURS have passed.
  useEffect(() => {
    if (!auto || !handle || permission !== 'granted') return undefined;
    const tick = () => {
      if (scanIsDue(companyId)) check(handle);
    };
    tick();
    const t = setInterval(tick, 60 * 60 * 1000);
    return () => clearInterval(t);
  }, [auto, handle, permission, companyId, check]);

  return {
    supported,
    handle,
    permission,
    // Access to the saved folder is in place on this computer.
    verified: !!handle && permission === 'granted',
    running,
    lastResult,
    verify,
    check,
    refresh,
    allow
  };
}
